import { apiFetch, apiFetchText } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  Supplier,
  CreateSupplierPayload,
  UpdateSupplierPayload,
  PageResponse,
  SupplierReportStatusesResponse,
  SupplierReportStatusParams,
  ReportReferenceStatus,
  PedidoExportDocument,
  PedidoStoreOption,
  MensalExportDocument,
  MonthlySupplierSummaryDocument,
  CaixasExportDocument,
  ManualSupplierBoxTransactionPayload,
  ManualSupplierBoxTransactionResult,
  SupplierOwnBoxBalance,
  SupplierBoxTransactionRow,
  SupplierBoxTransactionsParams,
} from "./types";

export type ListSuppliersParams = {
  limit?: number;
  offset?: number;
  search?: string;
};

export function listSuppliers(
  params: ListSuppliersParams = {},
  signal?: AbortSignal,
): Promise<PageResponse<Supplier>> {
  const limit = params.limit ?? 2000;
  const offset = params.offset ?? 0;
  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const search = params.search?.trim();
  if (search) qs.set("search", search);
  return apiFetch(`${ENDPOINTS.suppliers.list}?${qs.toString()}`, { signal });
}

export function createSupplier(
  payload: CreateSupplierPayload,
): Promise<Supplier> {
  return apiFetch(ENDPOINTS.suppliers.list, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSupplier(
  id: string,
  payload: UpdateSupplierPayload,
): Promise<Supplier> {
  return apiFetch(ENDPOINTS.suppliers.detail(id), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteSupplier(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.suppliers.detail(id), { method: "DELETE" });
}

export function createManualSupplierBoxTransaction(
  id: string,
  payload: ManualSupplierBoxTransactionPayload,
): Promise<ManualSupplierBoxTransactionResult> {
  return apiFetch(ENDPOINTS.suppliers.boxTransactions(id), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getSupplierOwnBoxBalance(): Promise<SupplierOwnBoxBalance> {
  return apiFetch(ENDPOINTS.supplierCaixas.balance);
}

export function listSupplierOwnBoxTransactions(
  params: SupplierBoxTransactionsParams,
  signal?: AbortSignal,
): Promise<PageResponse<SupplierBoxTransactionRow>> {
  const qs = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });

  if (params.search) qs.set("search", params.search);
  if (params.client) qs.set("client", params.client);
  if (params.store) qs.set("store", params.store);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);

  return apiFetch(`${ENDPOINTS.supplierCaixas.list}?${qs.toString()}`, {
    signal,
  });
}

export function getSupplierReportStatuses(
  supplierIds: string[],
  signal?: AbortSignal,
): Promise<SupplierReportStatusesResponse> {
  const normalizedIds = Array.from(
    new Set(supplierIds.map((id) => id.trim()).filter(Boolean)),
  ).sort();
  const qs = new URLSearchParams({ supplier_ids: normalizedIds.join(",") });
  return apiFetch(`${ENDPOINTS.suppliers.reportStatuses}?${qs.toString()}`, {
    signal,
  });
}

export function getSupplierReportStatus(
  id: string,
  params: SupplierReportStatusParams,
  signal?: AbortSignal,
): Promise<ReportReferenceStatus> {
  const qs = new URLSearchParams({ type: params.type });
  if (params.month) qs.set("month", params.month);
  if (params.date) qs.set("date", params.date);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiFetch(`${ENDPOINTS.suppliers.reportStatus(id)}?${qs.toString()}`, {
    signal,
  });
}

export function getReportMensal(id: string, month: string): Promise<string> {
  return apiFetchText(
    `${ENDPOINTS.suppliers.reportMensal(id)}?month=${encodeURIComponent(month)}`,
  );
}

export function getReportMensalData(
  id: string,
  month: string,
  commissionRate?: string,
): Promise<MensalExportDocument> {
  const qs = new URLSearchParams({ month });
  if (commissionRate?.trim()) qs.set("commission_rate", commissionRate.trim());
  return apiFetch<MensalExportDocument>(
    `${ENDPOINTS.suppliers.reportMensalData(id)}?${qs.toString()}`,
  ).then(normalizeMensalExportDocument);
}

export function getMonthlySupplierSummaryData(
  month: string,
  commissionRate?: string,
): Promise<MonthlySupplierSummaryDocument> {
  const qs = new URLSearchParams({ month });
  if (commissionRate?.trim()) qs.set("commission_rate", commissionRate.trim());
  return apiFetch<MonthlySupplierSummaryDocument>(
    `${ENDPOINTS.suppliers.reportMensalGeralData}?${qs.toString()}`,
  ).then(normalizeMonthlySupplierSummaryDocument);
}

export function getReportCaixas(
  id: string,
  from: string,
  to: string,
): Promise<string> {
  return apiFetchText(
    `${ENDPOINTS.suppliers.reportCaixas(id)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}

export function getReportCaixasData(
  id: string,
  from: string,
  to: string,
): Promise<CaixasExportDocument> {
  const qs = new URLSearchParams({ from, to });
  return apiFetch<CaixasExportDocument>(
    `${ENDPOINTS.suppliers.reportCaixasData(id)}?${qs.toString()}`,
  ).then(normalizeCaixasExportDocument);
}

export function getReportPedido(id: string, date: string): Promise<string> {
  return apiFetchText(
    `${ENDPOINTS.suppliers.reportPedido(id)}?date=${encodeURIComponent(date)}`,
  );
}

export function getReportPedidoData(
  id: string,
  date: string,
  storeIds?: string[],
): Promise<PedidoExportDocument> {
  const qs = new URLSearchParams({ date });
  if (storeIds) qs.set("store_ids", storeIds.join(","));
  return apiFetch<PedidoExportDocument>(
    `${ENDPOINTS.suppliers.reportPedidoData(id)}?${qs.toString()}`,
  ).then(normalizePedidoExportDocument);
}

export function getReportPedidoStores(
  id: string,
  date: string,
): Promise<PedidoStoreOption[]> {
  return apiFetch<PedidoStoreOption[]>(
    `${ENDPOINTS.suppliers.reportPedidoStores(id)}?date=${encodeURIComponent(date)}`,
  );
}

function normalizeMensalExportDocument(
  document: MensalExportDocument,
): MensalExportDocument {
  return {
    ...document,
    orders: document.orders ?? [],
    products: document.products ?? [],
  };
}

function normalizeMonthlySupplierSummaryDocument(
  document: MonthlySupplierSummaryDocument,
): MonthlySupplierSummaryDocument {
  return { ...document, suppliers: document.suppliers ?? [] };
}

function normalizeCaixasExportDocument(
  document: CaixasExportDocument,
): CaixasExportDocument {
  return { ...document, movements: document.movements ?? [] };
}

function normalizePedidoExportDocument(
  document: PedidoExportDocument,
): PedidoExportDocument {
  return {
    ...document,
    products: document.products ?? [],
    stores: (document.stores ?? []).map((store) => ({
      ...store,
      items: store.items ?? [],
    })),
  };
}
